import React, {useState, useMemo} from 'react'
import {startCase} from 'lodash'
import {subDays} from 'date-fns'
import {useLocation} from 'react-router-dom'
import {Box, Typography, Collapse} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import MapOutlinedIcon from '@mui/icons-material/MapOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import AssignmentReturnOutlinedIcon from '@mui/icons-material/AssignmentReturnOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined'

import withLayout from '../../hocs/Layout'
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined'
import DashboardDateFilter, {DateRangeCaption} from '../../common/DashboardDateFilter'
import PageTitle from '../../common/PageTitle'
import HeroKPIRow from './HeroKPIRow'
import DeliverySuccessDonut from './DeliverySuccessDonut'
import ShippingOverview from './ShippingOverview'
import Orders from './Orders'
import CourierPerformance from './courierPerformance'
import CourierComparison from './courierPerformance/CourierComparison'
import PtlCourierPerformance from './courierPerformance/PtlCourierPerformance'
import Lost from './Lost'
import {ShipmentAlerts} from '../ct'
import {formatCurrency, getYYYYMMDD, getOrderStatus, filterShipmentsByFlow} from '../../helpers/UtilityHelper'
import {getMerchant} from '../../helpers/ReduxHelpers'
import {useGetAllShipmentsWithPickupAndDeliveryDataQuery, useGetPtlOverviewStatsQuery} from '../../redux/commonRTK'
import {FONT, SIZE, WEIGHT} from '../../styles/foundation/typography'
import {NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE, SUCCESS, ERROR, WARNING} from '../../styles/foundation/colors'
import {SHADOW} from '../../styles/foundation/effects'
import {pageContainerSx, helpTextSx, helpTextTypeSx} from '../../styles/patterns'

const cardSx = {
  bgcolor: WHITE, borderRadius: '16px', border: `1px solid ${BORDER}`,
  boxShadow: SHADOW.card, overflow: 'hidden', mb: 2.5,
  transition: 'box-shadow 0.2s', '&:hover': {boxShadow: SHADOW.cardHover},
}
const cardHeaderSx = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  px: 2.5, py: 2, cursor: 'pointer', '&:hover': {bgcolor: '#fafbfc'},
}

// Merchant-zone codes (from freightsnapshot data) → human labels + display order.
const ZONE_LABELS = {
  LOCAL: 'Local', REGIONAL: 'Regional', METRO: 'Metro', METROS: 'Metro',
  ROI: 'Rest of India', NATIONAL: 'National', SPECIAL: 'Special Zone',
}
const ZONE_ORDER = ['LOCAL', 'REGIONAL', 'METRO', 'METROS', 'ROI', 'NATIONAL', 'SPECIAL']

// ── Section titles ──────────────────────────────────────────────
const SECTIONS = {
  // D2C Dashboard sections
  overview: {title: 'D2C Shipment Overview', sub: 'Status distribution, revenue summary, and shipping cost trends'},
  shipments: {title: 'D2C Shipment Analytics', sub: 'Order-level analytics — status splits, payment modes, and trends'},
  'shipment-alerts': {title: 'Shipment Alerts', sub: 'Real-time monitoring of delivery, pickup, and RTO delays'},
  lost: {title: 'D2C Lost & Damaged', sub: 'Track lost, damaged, and missing D2C shipments'},
  // D2C Courier Performance
  courier: {title: 'Surface Performance', sub: 'D2C surface delivery courier-wise performance and TAT adherence'},
  air: {title: 'Air Performance', sub: 'D2C air mode courier-wise delivery % and TAT adherence'},
  sdd: {title: 'SDD Performance', sub: 'Same-Day Delivery courier performance and benchmarking'},
  'sc-ndd': {title: 'Same-City NDD', sub: 'Same-City Next-Day Delivery courier performance'},
  'ic-ndd': {title: 'Inter-City NDD', sub: 'Inter-City Next-Day Delivery courier performance'},
  'courier-comparison': {title: 'D2C Courier Comparison', sub: 'Side-by-side comparison of D2C courier partners'},
  // PTL Dashboard sections
  'ptl-overview': {title: 'PTL Shipment Overview', sub: 'PTL freight status distribution, revenue, and shipping cost trends'},
  'ptl-shipments': {title: 'PTL Shipment Analytics', sub: 'PTL order-level analytics — status splits, payment modes, and trends'},
  'ptl-alerts': {title: 'PTL Shipment Alerts', sub: 'Real-time monitoring of PTL delivery and pickup delays'},
  'ptl-lost': {title: 'PTL Lost & Damaged', sub: 'Track lost, damaged, and missing PTL shipments'},
  // PTL Courier Performance
  'ptl-courier-performance': {title: 'Non-ABD Courier Performance', sub: 'Non-ABD PTL courier-wise delivery % and TAT adherence'},
  'non-abd-comparison': {title: 'Non-ABD Courier Comparison', sub: 'Side-by-side comparison of Non-ABD PTL courier partners'},
  'ptl-courier-abd': {title: 'ABD Courier Performance', sub: 'ABD (Appointment-Based Delivery) courier performance'},
  'abd-comparison': {title: 'ABD Courier Comparison', sub: 'Side-by-side comparison of ABD courier partners'},
}


function ZoneWiseSplit({data, loading, flow = 'ALL'}) {
  const isReverse = flow === 'REVERSE';
  const [open, setOpen] = useState(true)
  const rows = Array.isArray(data) ? data : []
  const totalRevenue = rows.reduce((s, z) => s + z.revenue, 0)
  const totalCost = rows.reduce((s, z) => s + z.shippingCost, 0)
  const totalOrders = rows.reduce((s, z) => s + z.orders, 0)
  const overallPct = totalRevenue ? ((totalCost / totalRevenue) * 100).toFixed(1) : 0

  // Zone health from the shipping-cost ratio (healthy ≤ 12%, watch ≤ 18%, else high).
  const zoneStatus = (pct) => (pct <= 12 ? {label: 'Healthy', color: SUCCESS} : pct <= 18 ? {label: 'Watch', color: WARNING} : {label: 'High', color: ERROR})
  const ZG = '1.5fr 0.9fr 1fr 1fr 0.9fr 0.9fr' // table columns: Zone · Orders · Revenue · Shipping · Shipping% · Status

  return (
    <Box sx={cardSx}>
      <Box sx={cardHeaderSx} onClick={() => setOpen((v) => !v)}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
          <MapOutlinedIcon sx={{color: PRIMARY_BLUE, fontSize: 22}} />
          <Box>
            <Typography sx={{fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY}}>
              {isReverse ? 'Invoice Value vs Pickup Cost — Zone-wise Split' : 'Revenue vs Shipping Cost — Zone-wise Split'}
            </Typography>
            <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.3}}>
              {isReverse ? 'Pickup cost as percentage of invoice value by zone' : 'Shipping cost as percentage of revenue by delivery zone'}
            </Typography>
          </Box>
        </Box>
        {open ? <ExpandLessIcon sx={{color: SUBTLE}} /> : <ExpandMoreIcon sx={{color: SUBTLE}} />}
      </Box>
      <Collapse in={open}>
        {loading ? (
          <Box sx={{px: 2.5, pb: 3, pt: 1, textAlign: 'center'}}>
            <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE}}>
              Loading zone-wise data…
            </Typography>
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{px: 2.5, pb: 3, pt: 1, textAlign: 'center'}}>
            <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE}}>
              No zone-wise data for the selected vertical and date range.
            </Typography>
          </Box>
        ) : (
        <Box sx={{px: 2.5, pb: 2.5}}>
          <Box sx={helpTextSx}>
            <InfoOutlinedIcon sx={{fontSize: 16, color: PRIMARY_BLUE, mt: 0.2}} />
            <Typography sx={helpTextTypeSx}>
              {isReverse
                ? <><strong>How to read:</strong> Each zone shows invoice value, pickup cost, and the ratio. A healthy ratio is 5–12% for local and 10–18% for national.</>
                : <><strong>How to read:</strong> Each zone shows revenue, shipping cost, and the ratio. A healthy ratio is 5–12% for local and 10–18% for national.
                  {' '}<strong>Total Revenue</strong> here is invoice value across <strong>all</strong> shipments in this view — including RTO, Cancelled, and Lost — so it can differ from "Sales Revenue" in the Overview KPIs above, which excludes those.</>}
            </Typography>
          </Box>
          <Box sx={{
            display: 'grid', gridTemplateColumns: {xs: 'repeat(2,1fr)', sm: 'repeat(4,1fr)'},
            bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '10px', overflow: 'hidden',
            boxShadow: SHADOW.card, mb: 2.5,
          }}>
            {[
              // `full` = exact value shown on hover — the compact display (₹10L) otherwise
              // rounds two genuinely different totals to the same figure, hiding the gap.
              {label: isReverse ? 'Total Invoice Value' : 'Total Revenue', value: formatCurrency(totalRevenue, true), full: formatCurrency(totalRevenue), color: SUCCESS},
              {label: isReverse ? 'Total Pickup Cost' : 'Total Shipping', value: formatCurrency(totalCost, true), full: formatCurrency(totalCost), color: WARNING},
              {label: isReverse ? 'Pickup Cost %' : 'Shipping %', value: `${overallPct}%`, color: parseFloat(overallPct) > 15 ? ERROR : PRIMARY_BLUE},
              {label: 'Total Orders', value: totalOrders.toLocaleString(), color: PRIMARY_BLUE},
            ].map((s) => (
              <Box key={s.label} sx={{p: 1.75, borderRight: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
                  <Box sx={{width: 7, height: 7, borderRadius: '2px', bgcolor: s.color, flex: 'none'}} />
                  <Typography sx={{fontFamily: FONT, fontSize: '9.5px', fontWeight: WEIGHT.extra, letterSpacing: '0.05em', textTransform: 'uppercase', color: SUBTLE}}>{s.label}</Typography>
                </Box>
                <Typography title={s.full || undefined} sx={{fontFamily: FONT, fontSize: '22px', fontWeight: WEIGHT.extra, color: s.color, lineHeight: 1, fontVariantNumeric: 'tabular-nums', mt: 0.75, cursor: s.full ? 'help' : 'default'}}>{s.value}</Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{border: `1px solid ${BORDER}`, borderRadius: '10px', overflow: 'hidden'}}>
            <Box sx={{width: '100%', overflowX: 'auto'}}>
              <Box sx={{minWidth: 640}}>
                {/* header */}
                <Box sx={{display: 'grid', gridTemplateColumns: ZG, gap: 1.5, px: 2, py: 1.5, bgcolor: '#f7f9fc', borderBottom: `1px solid ${BORDER}`}}>
                  {[['Zone', 'left'], ['Orders', 'right'], [isReverse ? 'Invoice Value (₹)' : 'Revenue (₹)', 'right'], [isReverse ? 'Pickup Cost (₹)' : 'Shipping (₹)', 'right'], [isReverse ? 'Pickup Cost %' : 'Shipping %', 'right'], ['Status', 'center']].map(([h, a]) => (
                    <Typography key={h} sx={{fontFamily: FONT, fontSize: '10px', fontWeight: WEIGHT.extra, letterSpacing: '0.05em', textTransform: 'uppercase', color: SUBTLE, textAlign: a}}>{h}</Typography>
                  ))}
                </Box>
                {/* rows */}
                {rows.map((z) => {
                  const pct = z.revenue ? (z.shippingCost / z.revenue) * 100 : 0
                  const st = zoneStatus(pct)
                  return (
                    <Box key={z.zone} sx={{display: 'grid', gridTemplateColumns: ZG, gap: 1.5, px: 2, py: 1.4, alignItems: 'center', borderBottom: `1px solid ${BORDER}`}}>
                      <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: NAVY}}>{z.zone}</Typography>
                      <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: NAVY, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>{z.orders.toLocaleString()}</Typography>
                      <Typography title={formatCurrency(z.revenue)} sx={{fontFamily: FONT, fontSize: SIZE.xs, color: NAVY, textAlign: 'right', fontVariantNumeric: 'tabular-nums', cursor: 'help'}}>{formatCurrency(z.revenue, true)}</Typography>
                      <Typography title={formatCurrency(z.shippingCost)} sx={{fontFamily: FONT, fontSize: SIZE.xs, color: NAVY, textAlign: 'right', fontVariantNumeric: 'tabular-nums', cursor: 'help'}}>{formatCurrency(z.shippingCost, true)}</Typography>
                      <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: st.color, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>{pct.toFixed(1)}%</Typography>
                      <Box sx={{textAlign: 'center'}}>
                        <Box component='span' sx={{fontFamily: FONT, fontSize: '11px', fontWeight: WEIGHT.bold, color: st.color, bgcolor: `${st.color}1f`, px: 1, py: 0.3, borderRadius: '999px'}}>{st.label}</Box>
                      </Box>
                    </Box>
                  )
                })}
                {/* total */}
                <Box sx={{display: 'grid', gridTemplateColumns: ZG, gap: 1.5, px: 2, py: 1.5, alignItems: 'center', bgcolor: '#f2f5fa'}}>
                  <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.extra, color: NAVY}}>Total</Typography>
                  <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.extra, color: NAVY, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>{totalOrders.toLocaleString()}</Typography>
                  <Typography title={formatCurrency(totalRevenue)} sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.extra, color: NAVY, textAlign: 'right', fontVariantNumeric: 'tabular-nums', cursor: 'help'}}>{formatCurrency(totalRevenue, true)}</Typography>
                  <Typography title={formatCurrency(totalCost)} sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.extra, color: NAVY, textAlign: 'right', fontVariantNumeric: 'tabular-nums', cursor: 'help'}}>{formatCurrency(totalCost, true)}</Typography>
                  <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.extra, color: NAVY, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>{overallPct}%</Typography>
                  <Box />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        )}
      </Collapse>
    </Box>
  )
}

// ── URL → section mapping ───────────────────────────────────────
const D2C_SECTION_MAP = {
  dashboard: 'overview', 'd2c-dashboard': 'overview', shipments: 'shipments', 'shipment-alerts': 'shipment-alerts', lost: 'lost',
  courier: 'courier', air: 'air', sdd: 'sdd', 'sc-ndd': 'sc-ndd', 'ic-ndd': 'ic-ndd',
  'courier-comparison': 'courier-comparison',
}
const PTL_SECTION_MAP = {
  'ptl-dashboard': 'ptl-overview', 'ptl-shipments': 'ptl-shipments',
  'ptl-alerts': 'ptl-alerts', 'ptl-lost': 'ptl-lost',
  'ptl-courier-performance': 'ptl-courier-performance',
  'non-abd-comparison': 'non-abd-comparison',
  abd: 'ptl-courier-abd', 'abd-comparison': 'abd-comparison',
}
const COMBINED_MAP = {...D2C_SECTION_MAP, ...PTL_SECTION_MAP}

// Courier-performance + comparison sections — each renders its OWN scoped summary,
// so the generic dashboard Hero KPI row is hidden here (it isn't section-scoped and
// would otherwise show the same overall totals across all of them).
const COURIER_PERF_SECTIONS = [
  'courier', 'air', 'sdd', 'sc-ndd', 'ic-ndd', 'courier-comparison',
  'ptl-courier-performance', 'non-abd-comparison', 'ptl-courier-abd', 'abd-comparison',
]

// Sections that manage their own filters (no global filter bar)
const NO_FILTER_SECTIONS = [
  'shipment-alerts', 'ptl-alerts',
  'lost', 'ptl-lost', // Lost & Damaged renders its own filter bar + KPIs
  ...COURIER_PERF_SECTIONS,
]

// ── Main Dashboard ──────────────────────────────────────────────
const Dashboard = () => {
  const {pathname} = useLocation()
  // Dashboard mode from URL:
  //   /ptl-dashboard = PTL-only · /d2c-dashboard = D2C-only · /dashboard = Common (D2C+PTL tabs)
  const mode = pathname.startsWith('/ptl-dashboard') ? 'ptl' : pathname.startsWith('/d2c-dashboard') ? 'd2c' : 'common'
  const [vertical, setVertical] = useState('B2C') // Common-dashboard D2C/PTL tab
  const [flow, setFlow] = useState('FORWARD') // D2C forward/reverse (RVP) split — 'FORWARD' | 'REVERSE'
  const freightType = mode === 'ptl' ? 'B2B' : mode === 'd2c' ? 'B2C' : vertical
  const [dateRange, setDateRange] = useState([{
    endDate: subDays(new Date(), 1), startDate: subDays(new Date(), 7), key: 'selection',
  }])

  const merchant = getMerchant()
  // Honor the explicit vertical (route / Common-dashboard tab). Don't clamp to the
  // merchant's flags via getShipmentInfo — that made single-vertical merchants show
  // the same data on both D2C and PTL tabs.
  const shipmentInfo = freightType

  // Determine active section from URL
  const lastSegment = pathname.split('/').filter(Boolean).pop()
  const activeSection = COMBINED_MAP[lastSegment] || (freightType === 'B2B' ? 'ptl-overview' : 'overview')
  // On the Common dashboard, show the PTL section copy when the PTL tab is active.
  const PTL_META_KEY = {overview: 'ptl-overview', shipments: 'ptl-shipments', 'shipment-alerts': 'ptl-alerts', lost: 'ptl-lost'}
  const metaSection = (mode === 'common' && freightType === 'B2B' && PTL_META_KEY[activeSection]) || activeSection
  const sectionMeta = SECTIONS[metaSection] || SECTIONS.overview
  const dashLabel = mode === 'ptl' ? 'PTL Dashboard' : mode === 'd2c' ? 'D2C Dashboard' : 'Dashboard'
  // The D2C/PTL tab only applies to the Common dashboard's data sections.
  const showVerticalTab = mode === 'common' && ['overview', 'shipments', 'shipment-alerts', 'lost'].includes(activeSection)
  // Forward/Reverse (RVP) split — D2C only, and only on the shared-query data sections
  // (Overview + Shipment Analytics). PTL has no reverse flow, so it's hidden there.
  const showFlowTab = freightType === 'B2C' && ['overview', 'shipments'].includes(activeSection)

  // Wire the hero KPI row to real shipment data — same query the dashboard
  // sections use, so RTK Query dedupes it into a single network call.
  const kpiPayload = {
    from_date: getYYYYMMDD(dateRange[0].startDate),
    to_date: getYYYYMMDD(dateRange[0].endDate),
    merchant_list: merchant?.id,
    shipmentType_list: shipmentInfo,
  }
  // Use `currentData` (not `data`): when the vertical/date arg changes, `data` keeps
  // returning the PREVIOUS arg's value until the new (slow) fetch resolves, which made
  // the PTL tab show stale D2C counts until a reload. `currentData` is undefined for the
  // in-flight arg, so we can render a loading state instead of stale numbers.
  const {currentData: kpiShipments, isFetching: kpiFetching} =
    useGetAllShipmentsWithPickupAndDeliveryDataQuery(kpiPayload, {
      // Courier-performance sections hide the Hero KPI row / zone split, so don't fetch its data there.
      skip: !dateRange[0].startDate || !dateRange[0].endDate || COURIER_PERF_SECTIONS.includes(activeSection),
    })
  // Scope the shared shipment list to the active flow (Forward / Reverse) for D2C.
  const flowShipments = useMemo(
    () => (Array.isArray(kpiShipments) && showFlowTab ? filterShipmentsByFlow(kpiShipments, flow) : kpiShipments),
    [kpiShipments, showFlowTab, flow],
  )

  const kpiData = useMemo(() => {
    if (!Array.isArray(flowShipments)) return null
    let pendingPickups = 0, inTransit = 0, ndrPending = 0, nprPending = 0, delivered = 0, rto = 0
    flowShipments.forEach((s) => {
      const st = getOrderStatus(s.orderStatus)
      if (st === 'ORDER_PLACED') {
        pendingPickups++
        if (s.orderStatus === 'PICKUP_FAILED') nprPending++
      }
      else if (st === 'INTRANSIT') inTransit++
      else if (st === 'DELIVERY_FAILED') ndrPending++
      else if (st === 'DELIVERED') delivered++
      else if (st === 'RTO') rto++
    })
    return {totalShipments: flowShipments.length, pendingPickups, inTransit, ndrPending, nprPending, delivered, rto}
  }, [flowShipments])

  // PTL overview live stats (Breached Non-ABD + Stuck Non-ABD) from the pending CT
  // freightsnapshots. PTL-only, current-state metrics (not date-range scoped).
  // ABD stuck (first-EDD-breached) is pending a backend EDD field — see docs/NEW_APIS_NEEDED.md.
  const isPtlOverview = freightType === 'B2B' && ['overview', 'ptl-overview'].includes(activeSection)
  const {data: ptlStats} = useGetPtlOverviewStatsQuery(merchant?.id, {
    skip: !merchant?.id || !isPtlOverview,
  })
  const kpiDataWithStuck = useMemo(
    () => (kpiData ? {...kpiData, breached: ptlStats?.breached || 0, stuckNonAbd: ptlStats?.stuckNonAbd || 0} : kpiData),
    [kpiData, ptlStats],
  )

  // Real zone-wise revenue vs shipping cost (grouped from the same shipments).
  const zoneRows = useMemo(() => {
    if (!Array.isArray(flowShipments) || flowShipments.length === 0) return null
    const byZone = {}
    flowShipments.forEach((s) => {
      const z = s.merchantZone
      if (!z || z === 'undefined') return
      if (!byZone[z]) byZone[z] = {revenue: 0, shippingCost: 0, orders: 0}
      byZone[z].revenue += Number(s.invoiceValue) || 0
      byZone[z].shippingCost += Number(s.price) || 0
      byZone[z].orders += 1
    })
    return Object.keys(byZone)
      .sort((a, b) => {
        const ia = ZONE_ORDER.indexOf(a), ib = ZONE_ORDER.indexOf(b)
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib)
      })
      .map((z) => ({
        zone: ZONE_LABELS[z] || startCase(z.toLowerCase()),
        ...byZone[z],
      }))
  }, [flowShipments])

  return (
    <Box sx={pageContainerSx}>
      <PageTitle subTitle={sectionMeta.sub}>{dashLabel} — {sectionMeta.title}</PageTitle>

      {/* Vertical split (D2C / PTL) as underline tabs — the primary segregation — with the
          Forward / Reverse direction beneath (D2C only). */}
      {(showVerticalTab || showFlowTab) && (
        <Box sx={{bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '16px', boxShadow: SHADOW.card, px: 2.5, py: 2, mb: 2.5}}>
          {showVerticalTab && (
            <Box sx={{display: 'flex', gap: 3.5, borderBottom: `1px solid ${BORDER}`, mb: showFlowTab ? 2 : 0}}>
              {[{v: 'B2C', l: 'D2C', icon: <Inventory2OutlinedIcon />}, {v: 'B2B', l: 'PTL', icon: <WarehouseOutlinedIcon />}].map((o) => {
                const on = vertical === o.v
                return (
                  <Box key={o.v} onClick={() => setVertical(o.v)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1, pb: 1.25, px: 0.25, cursor: 'pointer',
                      fontFamily: FONT, fontSize: '14px', fontWeight: on ? WEIGHT.bold : WEIGHT.semibold,
                      color: on ? NAVY : SUBTLE, borderBottom: `2px solid ${on ? PRIMARY_BLUE : 'transparent'}`, mb: '-1px',
                      transition: 'color 0.15s', '& svg': {fontSize: 17},
                    }}>
                    {o.icon}{o.l}
                  </Box>
                )
              })}
            </Box>
          )}
          {showFlowTab && (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25}}>
              <Typography sx={{fontFamily: FONT, fontSize: '10.5px', fontWeight: WEIGHT.extra, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#a3adb8'}}>Direction</Typography>
              <Box role="radiogroup" aria-label="Shipment direction" sx={{display: 'inline-flex', bgcolor: '#f3f6fa', borderRadius: '9px', p: '3px', gap: '3px'}}>
                {[
                  {v: 'FORWARD', l: 'Forward', icon: <LocalShippingOutlinedIcon />},
                  {v: 'REVERSE', l: 'Reverse', icon: <AssignmentReturnOutlinedIcon />},
                ].map((o) => {
                  const on = flow === o.v
                  return (
                    <Box key={o.v}
                      role="radio"
                      aria-checked={on}
                      aria-label={o.l}
                      tabIndex={0}
                      onClick={() => setFlow(o.v)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFlow(o.v) }
                        // Arrow keys move between the two directions, standard radiogroup behaviour.
                        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                          e.preventDefault(); setFlow(flow === 'FORWARD' ? 'REVERSE' : 'FORWARD')
                        }
                      }}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 0.75, px: 1.6, py: 0.75, borderRadius: '7px',
                        cursor: 'pointer', userSelect: 'none', fontFamily: FONT, fontSize: '12px', fontWeight: WEIGHT.semibold,
                        color: on ? NAVY : SUBTLE, bgcolor: on ? WHITE : 'transparent',
                        boxShadow: on ? '0 1px 3px rgba(20,35,55,0.12)' : 'none',
                        transition: 'all 0.15s', '& svg': {fontSize: 14},
                        '&:focus-visible': {outline: `2px solid ${PRIMARY_BLUE}`, outlineOffset: '2px'},
                      }}>
                      {o.icon}{o.l}
                    </Box>
                  )
                })}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Filter bar — Filters + active-range caption on the left, presets + picker on the right. */}
      {!NO_FILTER_SECTIONS.includes(activeSection) && !['lost', 'ptl-lost'].includes(activeSection) && (
        <Box sx={{
          bgcolor: WHITE, borderRadius: '14px', boxShadow: SHADOW.card,
          border: `1px solid ${BORDER}`, px: 2.5, py: 1.5, mb: 2.5,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5,
        }}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap'}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
              <TuneOutlinedIcon sx={{color: SUBTLE, fontSize: 20}} />
              <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.semibold, color: NAVY}}>Filters</Typography>
            </Box>
            <DateRangeCaption dateRange={dateRange} />
          </Box>
          <DashboardDateFilter
            dateRange={dateRange}
            maxDate={subDays(new Date(), 1)}
            onChange={(range) => setDateRange(range)}
          />
        </Box>
      )}

      {/* Hero KPI cards — hidden where the section has its own KPIs / no date filter (Shipments, Lost, Alerts, Courier perf) */}
      {!['shipments', 'ptl-shipments', 'lost', 'ptl-lost', 'shipment-alerts', 'ptl-alerts', ...COURIER_PERF_SECTIONS].includes(activeSection) && <HeroKPIRow data={kpiDataWithStuck} loading={kpiFetching} vertical={freightType} flow={showFlowTab ? flow : 'ALL'} />}

      {isPtlOverview && (
        <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1, p: 1.25, mb: 2, borderRadius: '8px', bgcolor: '#f0f4ff', border: '1px solid #c5d8f6'}}>
          <InfoOutlinedIcon sx={{fontSize: 16, color: PRIMARY_BLUE, mt: 0.2}} />
          <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: '#455A64', lineHeight: 1.5}}>
            <Box component='span' sx={{fontWeight: WEIGHT.bold}}>Note:</Box> <strong>Breached (Non-ABD)</strong> and <strong>Stuck (Non-ABD)</strong> are
            live counts of Non-ABD PTL shipments currently in the pipeline — Breached = SLA breached, Stuck = no status update in 24h+.
            They reflect the current position and are <strong>not</strong> filtered by the date range above. ABD shipments are excluded.
          </Typography>
        </Box>
      )}

      {/* Delivery-success donut — status outcome share, respects the flow toggle */}
      {(activeSection === 'overview' || activeSection === 'ptl-overview') && !kpiFetching && Array.isArray(flowShipments) && (
        <DeliverySuccessDonut
          data={flowShipments}
          flow={showFlowTab ? flow : 'ALL'}
          subtitle={`outcome share of ${(flowShipments.length).toLocaleString()} ${showFlowTab ? (flow === 'REVERSE' ? 'reverse' : 'forward') + ' ' : ''}shipments · basis: AWB registered date`}
        />
      )}

      {/* Zone-wise split — only on overview */}
      {(activeSection === 'overview' || activeSection === 'ptl-overview') && <ZoneWiseSplit data={zoneRows} loading={kpiFetching} flow={showFlowTab ? flow : 'ALL'} />}

      {/* Section content */}
      {(activeSection === 'overview' || activeSection === 'ptl-overview') && <ShippingOverview dateRange={dateRange} shipType={shipmentInfo} flow={showFlowTab ? flow : 'ALL'} />}
      {(activeSection === 'shipments' || activeSection === 'ptl-shipments') && <Orders dateRange={dateRange} shipType={shipmentInfo} flow={showFlowTab ? flow : 'ALL'} />}
      {(activeSection === 'shipment-alerts' || activeSection === 'ptl-alerts') && <ShipmentAlerts shipmentType={freightType} />}
      {(activeSection === 'lost' || activeSection === 'ptl-lost') && <Lost shipType={shipmentInfo} />}
      {['courier', 'air', 'sdd', 'sc-ndd', 'ic-ndd'].includes(activeSection) && <CourierPerformance section={lastSegment} />}
      {activeSection === 'courier-comparison' && <CourierComparison />}
      {/* PTL courier performance — live, segregated by ABD/Non-ABD via order/search
          (the CT freightsnapshot endpoints can't: shipmentType_list=PTL returns 0, and B2B
          is all-PTL + snapshot-only, so merchants with no live PTL activity show empty). */}
      {activeSection === 'ptl-courier-performance' && <PtlCourierPerformance abdMode='nonabd' view='performance' />}
      {activeSection === 'non-abd-comparison' && <PtlCourierPerformance abdMode='nonabd' view='comparison' />}
      {activeSection === 'ptl-courier-abd' && <PtlCourierPerformance abdMode='abd' view='performance' />}
      {activeSection === 'abd-comparison' && <PtlCourierPerformance abdMode='abd' view='comparison' />}
    </Box>
  )
}

export default withLayout(Dashboard)
